import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { CreateConditionBody } from '@/types/patient-chart-api';

/**
 * POST /api/Patients/CreateCondition
 *
 * Mirrors Flutter `patient_chart_data_source_impl.dart`
 * `createCondition()`. Wire body curl-verified by Andrew 2026-05-19.
 *
 * Body shape: see `CreateConditionBody` type doc — note the
 * Conditions-specific gotchas:
 * - `code.Definition` is PascalCase (capital D!)
 * - `bodySite` is an empty Coding `{system:null, code:null, display:null}`,
 *   never `null`
 * - `severity` is a bare string ("Mild" / "Moderate" / "Severe" / "n/a"),
 *   NOT a Coding
 * - `asserter` is the CURRENT user's `userId` (not a separate recorder)
 * - `performerDetails.speciality.specialitySkillId` ships the all-zero
 *   GUID sentinel
 * - `createdDate` is CLIENT-SET (`new Date().toISOString()` minus `Z`)
 *
 * Cache: invalidates `['patient-conditions', patientId]` (prefix match —
 * covers both Active and Resolved partitions). SignalR
 * `UpdatePatientChat` "Conditions" event from PROD-827 Slice 3 also
 * fires for concurrent sessions.
 */

interface CreateConditionParams {
  patientId: string;
  body: CreateConditionBody;
}

export function useCreateCondition() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateConditionParams>({
    mutationFn: ({ body }) =>
      post<string>('/api/Patients/CreateCondition', body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-conditions', patientId]);
    },
  });
}
